/**
 * Utilità per la generazione di colori per i marker della mappa
 */

/**
 * Genera un colore pastello con buona visibilità basato su un indice.
 * Usa lo spazio colore HSL per distribuire uniformemente i colori.
 *
 * @param index - Indice del colore (0-based). Stesso indice = stesso colore
 * @param saturation - Saturazione (0-100). Default 70 per colori pastello vivaci
 * @param lightness - Luminosità (0-100). Default 65 per buona visibilità
 * @returns Stringa colore in formato hex (#RRGGBB)
 */
export function getMarkerColor(
    index: number,
    saturation: number = 70,
    lightness: number = 65
): string {
    // Golden ratio per distribuzione uniforme dei colori
    const goldenRatio = 0.618033988749895;

    // Calcola la tonalità(hue) distribuita uniformemente
    const hue = (index * goldenRatio * 360) % 360;

    return hslToHex(hue, saturation, lightness);
}

/**
 * Genera un colore completamente random con tonalitàpastello
 *
 * @param saturation - Saturazione (0-100). Default 70
 * @param lightness - Luminosità (0-100). Default 65
 * @returns Stringa colore in formato hex (#RRGGBB)
 */
export function getRandomMarkerColor(
    saturation: number = 70,
    lightness: number = 65
): string {
    const hue = Math.random() * 360;
    return hslToHex(hue, saturation, lightness);
}

/**
 * Genera un array di N colori ben distribuiti
 *
 * @param count - Numero di colori da generare
 * @param saturation - Saturazione (0-100). Default 70
 * @param lightness - Luminosità(0-100). Default 65
 * @returns Array di colori in formato hex
 */
export function generateMarkerColorPalette(
    count: number,
    saturation: number = 70,
    lightness: number = 65
): string[] {
    return Array.from({ length: count }, (_, i) =>
        getMarkerColor(i, saturation, lightness)
    );
}

/**
 * Converte HSL in esadecimale
 *
 * @param h - Tonalità(0-360)
 * @param s - Saturazione (0-100)
 * @param l - Luminosità(0-100)
 * @returns Stringa colore in formato hex (#RRGGBB)
 */
function hslToHex(h: number, s: number, l: number): string {
    const sNorm = s / 100;
    const lNorm = l / 100;

    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;

    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 60) {
        r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
        r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
        r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
        r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
        r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
        r = c; g = 0; b = x;
    }

    const rHex = Math.round((r + m) * 255).toString(16).padStart(2, '0');
    const gHex = Math.round((g + m) * 255).toString(16).padStart(2, '0');
    const bHex = Math.round((b + m) * 255).toString(16).padStart(2, '0');

    return `#${rHex}${gHex}${bHex}`;
}

// ============================================
// Palette predefinita (OPZIONALE)
// ============================================

/**
 * Palette di 100 colori pastello predefiniti con buona visibilità
 * Generata usando la funzione getMarkerColor per consistenza
 */
export const MARKER_COLOR_PALETTE: readonly string[] = generateMarkerColorPalette(100);

/**
 * Ottiene un colore dalla palette predefinita
 *
 * @param index - Indice del colore (ciclico se > 99)
 * @returns Colore in formato hex
 */
export function getColorFromPalette(index: number): string {
    return MARKER_COLOR_PALETTE[index % MARKER_COLOR_PALETTE.length];
}
